import { useState } from 'react'
import { shallowEqual } from 'react-redux'
import { tabNames } from '../Constants'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useServiceTypes } from '../../../hooks/useServiceTypes'
import { useAppSelector } from '../../../redux/hooks/useAppSelector'

type Tab = {
  name: string
  isActive: boolean
}

export const useMemberStoreValues = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const [tabs, setTabs] = useState<Tab[]>([
    { name: tabNames.GENERAL, isActive: true },
    { name: tabNames.LICENSE_AND_PRODUCTIVITY, isActive: false },
    { name: tabNames.CALENDAR, isActive: false },
  ])

  const loadingServiceType = useAppSelector((state) => state.viewService.loading)
  const currentAdmin = useAppSelector((state) => state.accountSettings.adminProfile)
  const isLoggedIn = useAppSelector((state) => state.auth.accessToken)
  const { serviceTypes, isLoading: serviceLoading } = useServiceTypes()

  function setMemberProviderTabs(tabName: string, navigate = true, state: unknown = undefined) {
    const isTab = tabs.some((tab) => tab.name === tabName)
    const newSearchParams = new URLSearchParams(searchParams)

    if (isTab) {
      setTabs((prev) => prev.map((tab) => ({ ...tab, isActive: tab.name === tabName })))
      newSearchParams.set('tab', tabName)
    } else {
      newSearchParams.set('tab', tabNames.GENERAL)
    }

    setSearchParams(newSearchParams, { replace: navigate, state: state ?? location.state })
  }

  // `member` and `phoneIsValid` are no longer read from Redux — the member is read via the
  // useMember React Query hook and phoneIsValid is local state owned by useValidateMemberPhone.
  // `loading` (state.member.loading) is still driven by the phone-validation actions, so it is
  // kept to preserve the page spinner during validation.
  const memberValues = useAppSelector(
    (state) => ({
      loading: state.member.loading,
      error: state.member.error,
    }),
    shallowEqual,
  )

  const newClinicMemberValues = useAppSelector(
    (state) => ({
      // newClinicMember has no `loading` field — derive it from pageState for the member page spinner
      editMemberLoading: state.newClinicMember.pageState === 'loading',
      pageState: state.newClinicMember.pageState,
      isUserDataChanged: state.newClinicMember.isUserDataChanged,
      errorNewMember: state.newClinicMember.error,
      duplicateLicensesInfo: state.newClinicMember.duplicateLicensesInfo,
    }),
    shallowEqual,
  )

  return {
    loadingServiceType,
    currentAdmin,
    isLoggedIn,
    serviceTypes,
    serviceLoading,
    ...memberValues,
    ...newClinicMemberValues,
    setMemberProviderTabs,
    tabs,
  }
}
